import { baseApi } from '@/store/api/baseApi';
import type {
  BulkEnquiryIdsPayload,
  BulkEnquiryStatusPayload,
  Enquiry,
  EnquiryListParams,
  PaginatedEnquiries,
  UpdateEnquiryStatusPayload,
} from '@/types/enquiry';

function buildEnquiryListQuery(params: EnquiryListParams): string {
  const searchParams = new URLSearchParams();
  searchParams.set('page', String(params.page ?? 1));
  searchParams.set('limit', String(params.limit ?? 10));
  searchParams.set('sortBy', params.sortBy ?? 'createdAt');
  searchParams.set('sortOrder', params.sortOrder ?? 'desc');
  if (params.search?.trim()) searchParams.set('search', params.search.trim());
  if (params.status && params.status !== 'all') searchParams.set('status', params.status);
  return searchParams.toString();
}

function normalizeEnquiry(raw: unknown): Enquiry {
  const record = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const factbooksRaw = Array.isArray(record.factbooks) ? record.factbooks : [];
  const formatsRaw = Array.isArray(record.formats) ? record.formats : [];

  return {
    id: String(record.id ?? ''),
    institutionName: String(record.institutionName ?? ''),
    institutionType: String(record.institutionType ?? ''),
    country: String(record.country ?? ''),
    state: String(record.state ?? ''),
    city: String(record.city ?? ''),
    gstin: typeof record.gstin === 'string' && record.gstin.trim() ? record.gstin : undefined,
    fullName: String(record.fullName ?? ''),
    designation:
      typeof record.designation === 'string' && record.designation.trim()
        ? record.designation
        : undefined,
    officialEmail: String(record.officialEmail ?? ''),
    phone: String(record.phone ?? ''),
    factbooks: factbooksRaw
      .map((line) => {
        const item = (line && typeof line === 'object' ? line : {}) as Record<string, unknown>;
        return {
          volumeName: String(item.volumeName ?? '').trim(),
          quantity: Number(item.quantity) > 0 ? Number(item.quantity) : 1,
        };
      })
      .filter((line) => line.volumeName),
    formats: formatsRaw.map((value) => String(value ?? '').trim()).filter(Boolean),
    requiredBy:
      typeof record.requiredBy === 'string' && record.requiredBy.trim()
        ? record.requiredBy
        : undefined,
    budgetCycle:
      typeof record.budgetCycle === 'string' && record.budgetCycle.trim()
        ? record.budgetCycle
        : undefined,
    additionalNotes:
      typeof record.additionalNotes === 'string' && record.additionalNotes.trim()
        ? record.additionalNotes
        : undefined,
    status: (record.status as Enquiry['status']) ?? 'new',
    submittedAt:
      typeof record.submittedAt === 'string'
        ? record.submittedAt
        : typeof record.createdAt === 'string'
          ? record.createdAt
          : undefined,
    createdAt: typeof record.createdAt === 'string' ? record.createdAt : undefined,
    updatedAt: typeof record.updatedAt === 'string' ? record.updatedAt : undefined,
  };
}

function normalizeEnquiryList(raw: unknown): PaginatedEnquiries {
  const root =
    raw && typeof raw === 'object' && 'results' in (raw as object)
      ? (raw as Record<string, unknown>)
      : raw && typeof raw === 'object' && 'data' in (raw as object)
        ? ((raw as { data: unknown }).data as Record<string, unknown>)
        : {};

  const resultsRaw = Array.isArray(root?.results) ? root.results : [];

  return {
    results: resultsRaw.map(normalizeEnquiry),
    total: Number(root?.total ?? 0),
    page: String(root?.page ?? '1'),
    limit: String(root?.limit ?? '10'),
    sortBy: String(root?.sortBy ?? 'createdAt'),
    sortOrder: root?.sortOrder === 'asc' ? 'asc' : 'desc',
  };
}

export const enquiriesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getEnquiries: builder.query<PaginatedEnquiries, EnquiryListParams>({
      query: (params) => ({ url: `/enquiries?${buildEnquiryListQuery(params)}` }),
      transformResponse: (response: unknown) => normalizeEnquiryList(response),
      providesTags: (result) =>
        result?.results
          ? [
              ...result.results.map(({ id }) => ({ type: 'Enquiries' as const, id })),
              { type: 'Enquiries', id: 'LIST' },
            ]
          : [{ type: 'Enquiries', id: 'LIST' }],
    }),
    getEnquiry: builder.query<Enquiry, string>({
      query: (id) => `/enquiries/${id}`,
      transformResponse: (response: unknown) => normalizeEnquiry(response),
      providesTags: (_result, _error, id) => [{ type: 'Enquiries', id }],
    }),
    updateEnquiryStatus: builder.mutation<Enquiry, { id: string; body: UpdateEnquiryStatusPayload }>({
      query: ({ id, body }) => ({ url: `/enquiries/update-status/${id}`, method: 'POST', body }),
      transformResponse: (response: unknown) => normalizeEnquiry(response),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Enquiries', id },
        { type: 'Enquiries', id: 'LIST' },
      ],
    }),
    deleteEnquiry: builder.mutation<void, string>({
      query: (id) => ({ url: `/enquiries/delete/${id}`, method: 'POST' }),
      invalidatesTags: [{ type: 'Enquiries', id: 'LIST' }],
    }),
    bulkUpdateEnquiryStatus: builder.mutation<{ count: number }, BulkEnquiryStatusPayload>({
      query: (body) => ({ url: '/enquiries/bulk-status', method: 'POST', body }),
      invalidatesTags: [{ type: 'Enquiries', id: 'LIST' }],
    }),
    bulkDeleteEnquiries: builder.mutation<{ count: number }, BulkEnquiryIdsPayload>({
      query: (body) => ({ url: '/enquiries/bulk-delete', method: 'POST', body }),
      invalidatesTags: [{ type: 'Enquiries', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetEnquiriesQuery,
  useGetEnquiryQuery,
  useUpdateEnquiryStatusMutation,
  useDeleteEnquiryMutation,
  useBulkUpdateEnquiryStatusMutation,
  useBulkDeleteEnquiriesMutation,
} = enquiriesApi;
